import { uid } from '../../../../lib/uid';

const REMARKS = [
  'Answers show clear gaps worth a plan.',
  'Reviewed with the supervisor; ready for a development plan.',
  'Responses are consistent; proceed to identifying needs.',
];

const GAPS = [
  'Needs practice applying this on the job.',
  'Knows the basics; has not yet led the work.',
  'Applies it with guidance; not yet independently.',
  'Has not had the chance to use it in a live assignment.',
];

/** Up to `max` distinct items, at random. */
function pickSome<T>(items: readonly T[], max: number): T[] {
  return [...items].sort(() => Math.random() - 0.5).slice(0, Math.min(max, items.length));
}

const pick = <T>(items: readonly T[]): T => items[Math.floor(Math.random() * items.length)];

/** Sample assessment remarks — synthetic, suffixed so each run is told apart. */
export function sampleAssessmentRemarks(): string {
  return `Sample assessment ${uid().slice(0, 6).toUpperCase()}. ${pick(REMARKS)}`;
}

/** One sample development need, as the Needs grid holds it (levels as strings, as the cells do). */
export interface SampleDevelopmentNeed {
  competencyId: string;
  currentLevel: string;
  targetLevel: string;
  gap: string;
}

/**
 * ONE sample development need — a plan takes one (product call, 12 Oct 2026) — from the LIVE
 * competency list, with a current level below its target (levels 0–4) and a synthetic gap. Still
 * a list, because that is how the form holds its needs.
 */
export function sampleDevelopmentNeeds(competencyIds: readonly string[]): SampleDevelopmentNeed[] {
  return pickSome(competencyIds, 1).map((competencyId) => {
    const current = Math.floor(Math.random() * 4);
    const target = current + 1 + Math.floor(Math.random() * (4 - current));

    return {
      competencyId,
      currentLevel: String(current),
      targetLevel: String(target),
      gap: `Sample gap ${uid().slice(0, 6).toUpperCase()}. ${pick(GAPS)}`,
    };
  });
}

const PLAN_TITLES = ['Leadership Development Plan', 'Technical Upskilling Plan', 'Communication Skills Plan', 'Digital Skills Plan'];

/** A sample plan title — synthetic, suffixed so each run is told apart. */
export function samplePlanTitle(): string {
  return `${pick(PLAN_TITLES)} ${uid().slice(0, 6).toUpperCase()}`;
}

const TRAINING_TITLES = ['Leadership Essentials', 'Technical Writing Clinic', 'Data Analysis Workshop', 'Coaching and Mentoring Course'];

/** A sample training recommendation title — synthetic, suffixed so each run is told apart. */
export function sampleTrainingTitle(): string {
  return `${pick(TRAINING_TITLES)} ${uid().slice(0, 6).toUpperCase()}`;
}

/**
 * A sample development plan DOCUMENT — synthetic, tagged so each run is told apart, and built only
 * from markup the editor's own toolbar produces (and the server's allow-list keeps): paragraphs,
 * bold, and a bulleted list.
 *
 * A rich-text editor is UNCONTROLLED after mount, so a form that generates this must REMOUNT its
 * editor (a `key` it bumps) for the sample to show — setting the value alone would not.
 */
export function samplePlanDocument(): string {
  const tag = uid().slice(0, 6).toUpperCase();

  return [
    `<p><strong>Sample development plan ${tag}</strong></p>`,
    '<p>Over the next six months the employee closes the identified gap through guided practice and one formal course.</p>',
    '<ul><li><p>Month 1–2: attend the recommended training.</p></li><li><p>Month 3–4: apply it on a live assignment, with a mentor.</p></li><li><p>Month 5–6: review progress against the target level.</p></li></ul>',
  ].join('');
}

/** A sample recommendation for the create form's grid — ONE line of plain text, as its cell holds. */
export function sampleTrainingRecommendationText(): string {
  return pick([
    'A two-day course covering the fundamentals, followed by a practical exercise.',
    'A half-day workshop, then a month applying it on a live assignment with a mentor.',
    'An online course taken at their own pace, reviewed against the target level at the end.',
  ]);
}

/** Sample training recommendation DETAILS — synthetic, the same markup rules as the plan document. */
export function sampleTrainingDetails(): string {
  const tag = uid().slice(0, 6).toUpperCase();

  return [
    `<p><strong>Sample recommendation ${tag}</strong></p>`,
    '<p>A two-day course covering the fundamentals, followed by a practical exercise.</p>',
    '<ul><li><p>Why: it addresses the gap identified on the plan.</p></li><li><p>When: within the next quarter.</p></li></ul>',
  ].join('');
}
